import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, Signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { differenceInSeconds } from 'date-fns';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { BehaviorSubject, map, Subject, takeUntil } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { Access } from 'src/app/interfaces/api/access';
import { Project } from 'src/app/interfaces/api/project';
import { Role } from 'src/app/interfaces/api/role';
import { AuthMethod, defaultUser, PutUser, User } from 'src/app/interfaces/api/user';
import { AccessService } from 'src/app/services/api/accesses/access.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { RoleService } from 'src/app/services/api/roles/role.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';

@Component({
    selector: 'app-edit-user',
    imports: [
        MatPaginator,
        MatCheckboxModule,
        TranslateModule,
        CommonModule,
        FormsModule,
        MatSelectModule,
        MatTableModule,
        MatChipsModule,
        MatToolbarModule,
        MatInputModule,
        MatButtonModule,
        MatSlideToggleModule,
        MatIconModule,
        MatTooltipModule,
        ButtonComponent,
        SearchBarComponent,
        SpinnerComponent,
        NoDataComponent
    ],
    standalone: true,
    templateUrl: './edit-user.component.html',
    styleUrl: './edit-user.component.scss'
})

export class EditUserComponent implements AfterViewInit, OnDestroy {
    // Services
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly roleService: RoleService = inject(RoleService);
    private readonly auth: AuthService = inject(AuthService);
    private readonly pop: Pop = inject(Pop);
    private readonly tools: Tools = inject(Tools);
    private readonly router: Router = inject(Router);
    private readonly userService: UserService = inject(UserService);
    private readonly projectService: ProjectService = inject(ProjectService);
    private readonly accessService: AccessService = inject(AccessService);
    private readonly route: ActivatedRoute = inject(ActivatedRoute);

    // Component API
    @ViewChild('projectPaginator') projectPaginator: MatPaginator = new MatPaginator();
    @ViewChild(MatSort) sort: MatSort = new MatSort();
    @ViewChild('userForm') userForm: any;

    // Variables
    userId: string = '';
    user: User = defaultUser;
    loggedUser: User = defaultUser;
    form: any = {};
    projects: Project[] = [];
    roles: Role[] = [];
    assignableRoles: Role[] = [];
    readonly roleOrder: Role['name'][] = ['guest', 'user', 'userAdmin', 'dev', 'admin'];
    projectsData: MatTableDataSource<Project> = new MatTableDataSource();
    projectsCount: number = 0;
    projectsColumns: string[] = ['name', 'database', 'accesses', 'actions'];
    loading = false;
    paginator: any = {
        pageIndex: 0,
        pageSize: 10
    };
    paginatorSbj: BehaviorSubject<any> = new BehaviorSubject(this.paginator);
    archivedColor = "warn";
    searchText: string = '';
    readonly authMethods: { value: AuthMethod; label: string; logo?: string; icon?: string }[] = [
        { value: 'password', label: 'users.auth_method_password', icon: 'password' },
        { value: 'google', label: 'users.auth_method_google', logo: 'img/logo/logo_google.svg' },
        { value: 'microsoft', label: 'users.auth_method_microsoft', logo: 'img/logo/logo_microsoft.png' }
    ];
    destroy$ = new Subject<void>();
    accesses: Access[] = [];
    availableAccesses: Access[] = [];
    requestCancel$: Subject<void> = new Subject<void>();
    passwordVisible: boolean = false;
    selectedRole: Role['name'] = 'user';

    // Signals
    readonly projectSignal: Signal<Project> = this.projectService.currentProjectSignal;
    readonly canEditAuthMethods: Signal<boolean> = this.auth.canManageProject;

    constructor() {
        this.loggedUser = this.auth.currentUser()

        this.roleService.getRoles({}).pipe(takeUntil(this.destroy$)).subscribe((roles: any) => {
            this.roles = roles;
            this.assignableRoles = this.roles
                .filter(role => this.canAssignRole(role.name))
                .sort((a, b) => this.roleOrder.indexOf(a.name) - this.roleOrder.indexOf(b.name));

            if (this.user.id && typeof this.user.role === 'string') {
                this.selectedRole = this.resolveRoleName(this.user.role);
            }
        });

        this.accessService.getAccesses({ all: 1 }).pipe(takeUntil(this.destroy$)).subscribe((accesses: any) => {
            this.accesses = accesses;

            this.availableAccesses = this.accesses.filter(access => !access.author ||
                (typeof access.author === 'object' && access.author !== null && 'id' in access.author && (access.author as { id: string }).id == this.loggedUser.id)
            );
        });

        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
            this.userId = params['userId'];

            if (this.userId) {
                this.userService.getUser(this.userId).pipe(takeUntil(this.destroy$)).subscribe((user: any) => {
                    if (!user.settings) {
                        user.settings = {};
                        user.settings['subscribeToReport'] = 'never';
                    }

                    this.selectedRole = this.resolveRoleName(user.role);
                    user.mfaEnabled = user.mfaEnabled ?? true;
                    user.allowedAuthMethods = user.allowedAuthMethods?.length ? user.allowedAuthMethods : ['password', 'google', 'microsoft'];
                    this.user = user;

                    if (!this.user.accesses)
                        this.user.accesses = [];

                    this.projects.sort((a, b) => differenceInSeconds(b.createdDate, a.createdDate))

                    this.projects.forEach(p => {
                        if (p.users && p.users.indexOf(this.user.id) >= 0) {
                            p.selected = true;
                        }
                    })

                    this.projects.sort((a, b) => (a.selected === b.selected) ? 0 : a.selected ? -1 : 1);
                })
            } else {
                this.resetForm();
            }
        })
    }

    ngAfterViewInit(): void {
        Promise.resolve().then(() => {
            this.loadProjects();
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    search(text: string = "") {
        if (text !== this.searchText) {
            this.searchText = text;
            this.projectPaginator.pageIndex = 0;
            this.loadProjects();
        }
    }

    loadProjects() {
        this.requestCancel$.next();
        this.loading = true;

        let query: any = {
            offset: this.projectPaginator ? this.projectPaginator.pageIndex * this.projectPaginator.pageSize : 0,
            limit: this.projectPaginator ? this.projectPaginator.pageSize : 10,
            users: this.user.id || this.userId || "",
            archived: true
        }

        const searchText = this.searchText && this.searchText.trim() !== '' ? this.searchText.trim() : "";

        this.projectService.searchAndCountProjects(searchText, query).pipe(takeUntil(this.requestCancel$)).subscribe({
            next: (res: { results: Project[], total: number }) => {
                this.projects = res.results;

                this.projectsData.data = this.projects;
                this.projectsCount = res.total;

                if (this.projectPaginator)
                    this.projectPaginator.length = res.total;

                let userId = query.users;

                if (userId)
                    this.projects.forEach(p => {
                        if (p.users && p.users.indexOf(userId.toString()) >= 0) {
                            p.selected = true;
                        }
                    })

                setTimeout(() => {
                    this.projects.forEach(p => {
                        this.setAccessOptions(p);
                    });
                }, 500);
                this.loading = false;
            },
            error: (err) => {
                console.error(err);
                this.loading = false;
            }
        });
    }


    initTable() {
        this.projectPaginator.page.pipe(map(() => {
            let projects = this.projects.slice(this.projectPaginator.pageIndex * this.projectPaginator.pageSize, (this.projectPaginator.pageIndex * this.projectPaginator.pageSize) + this.projectPaginator.pageSize);

            return projects;
        })).pipe(takeUntil(this.destroy$)).subscribe(data => {
            this.projectsData = new MatTableDataSource(data);
        });
    }

    onProjectSelected(project: Project) {
        if (!this.user.projects) this.user.projects = [];

        let userProjectIndex = this.user.projects.findIndex((p: string | Project) => {
            if (typeof p === 'object' && p !== null && 'id' in p) {
                return (p as Project).id == project.id;
            }
            return false;
        });

        if (userProjectIndex >= 0 && !project.selected) {
            this.user.projects.splice(userProjectIndex, 1)
        } else if (userProjectIndex == -1 && project.selected) {
            this.user.projects.push(project)
        }

        if (!this.user.subscriptions)
            this.user.subscriptions = [];

        let projectSubscriptionIndex = this.user.subscriptions.findIndex((s: any) => s.project == project.id);

        if (!this.user.settings["subscribeToReport"])
            this.user.settings["subscribeToReport"] = "never";

        let dailyReport = this.user.settings["subscribeToReport"]?.indexOf("daily") >= 0 || this.user.settings["subscribeToReport"]?.indexOf("all") >= 0;
        let weeklyReport = this.user.settings["subscribeToReport"]?.indexOf("weekly") >= 0 || this.user.settings["subscribeToReport"]?.indexOf("all") >= 0;
        let isInMailingList = project.mailingList?.includes(this.user.id) || false;

        if (!this.user.subscriptions)
            this.user.subscriptions = [];

        if (projectSubscriptionIndex >= 0 && !project.selected) {
            this.user.subscriptions.splice(projectSubscriptionIndex, 1);
        } else if (projectSubscriptionIndex == -1 && project.selected) {
            this.user.subscriptions.push({
                email: isInMailingList,
                dailyReport,
                weeklyReport,
                project: project.id
            });
        } else if (projectSubscriptionIndex >= 0 && project.selected) {
            this.user.subscriptions[projectSubscriptionIndex] = {
                project: project.id,
                email: isInMailingList,
                dailyReport,
                weeklyReport
            }
        }
    }

    updateProfile() {
        let user = { ...this.user };

        if (!user.projects || !user.projects.length) {
            this.pop.warning("Please select a project")
            return;
        }

        user.projects = user.projects.map(p => (p as Project).id);

        if (!user.currentProject)
            user.currentProject = user.projects[0];


        if (!user.accesses)
            user.accesses = [];

        user.accesses = user.accesses.filter(access => user.projects!.map(p => (p as Project).id).includes(access.project));

        if (!user.email || !user.firstName || !user.lastName || !user.job || !user.company) {
            this.pop.warning("Please fill all required inputs", "danger")
            return;
        }

        if (!this.tools.isEmailValid(user.email)) {
            this.pop.error(this.translate.instant("user.invalid_email"), "danger");
            return;
        }

        if (user.password && !this.tools.isPasswordValid(user.password)) {
            this.pop.warning(this.translate.instant('users.password_rules', { min: this.tools.passwordMinLength }));
            return;
        }

        if (!this.selectedRole || !this.canAssignRole(this.selectedRole)) {
            this.pop.error(this.translate.instant('users.role_not_allowed'));
            return;
        }

        if (!user.allowedAuthMethods || !user.allowedAuthMethods.length) {
            this.pop.warning(this.translate.instant('users.auth_method_last_one'));
            return;
        }

        this.loading = true;

        const queryUser: PutUser = {
            id: user.id,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            job: user.job,
            company: user.company,
            role: this.selectedRole,
            projects: user.projects,
            accesses: user.accesses,
            settings: user.settings,
            subscriptions: user.subscriptions,
            language: user.language,
            mfaEnabled: user.mfaEnabled,
            allowedAuthMethods: user.allowedAuthMethods
        };

        if (user.password && user.password.trim() !== '') {
            queryUser.password = user.password;
        }

        if (user.id) {
            this.userService.setUser(queryUser).pipe(takeUntil(this.destroy$)).subscribe({
                next: (res: any) => {
                    this.projectService.updateUserAccess(user.id).pipe(takeUntil(this.destroy$)).subscribe({
                        next: (res) => {
                            this.loading = false;
                            this.pop.success(this.translate.instant('users.updated'));
                        },
                        error: (err) => {
                            this.loading = false;
                            this.pop.error(err.toString())
                        }
                    })
                },
                error: (err) => {
                    this.loading = false;
                    let message = '';
                    if (typeof err.error === 'string') {
                        message = this.translate.instant(err.error);
                    } else if (typeof err === 'string') {
                        message = this.translate.instant(err);
                    } else {
                        message = this.translate.instant('error.unknown');
                    }
                    this.pop.error(message);
                }
            })
        } else {
            if (!user.settings["subscribeToReport"])
                user.settings["subscribeToReport"] = "never";

            if (user.settings["subscribeToReport"]?.indexOf("never") == -1) {
                let dailyReport = user.settings["subscribeToReport"]?.indexOf("daily") >= 0 || user.settings["subscribeToReport"]?.indexOf("all") >= 0;
                let weeklyReport = user.settings["subscribeToReport"]?.indexOf("weekly") >= 0 || user.settings["subscribeToReport"]?.indexOf("all") >= 0;

                user.subscriptions = [];

                user.projects!.forEach(p => {
                    user.subscriptions!.push({
                        email: false,
                        dailyReport,
                        weeklyReport,
                        project: typeof p === 'string' ? p : p.id
                    })
                })
            }

            this.loading = false;

            this.userService.addUser({ ...user, role: this.selectedRole }).pipe(takeUntil(this.destroy$)).subscribe({
                next: (res: any) => {
                    this.pop.success(this.translate.instant('users.created'));
                    this.resetForm();

                    if (res.user && res.user.id) {
                        this.projectService.updateUserAccess(res.user.id).pipe(takeUntil(this.destroy$)).subscribe({
                            next: (res) => {
                                this.loading = false;
                                this.router.navigate(['/users/registry'])
                            },
                            error: (err) => {
                                this.loading = false;
                                this.pop.error(err.toString())
                            }
                        })
                    } else if (res.user && res.user.exist) {
                        this.loading = false;
                        this.pop.warning("User already exists")
                    } else {
                        this.loading = false;
                        this.router.navigate(['/users/registry'])
                    }
                },
                error: (err) => {
                    this.loading = false;
                    this.pop.error(this.translate.instant(err.error ? err.error : err.toString()))
                }
            })
        }
    }

    close() {
        this.router.navigate(['/users/registry'])
    }

    setAccessOptions(project: any) {
        if (!this.user.accesses)
            this.user.accesses = [];

        project.accesses = this.user.accesses
            .filter(access => access.project == project.id)
            .map(a => this.accesses.find(access => access.id == (typeof a.access === 'string' ? a.access : a.access.id)))
            .filter((a): a is Access => a !== undefined);

        project.access = project.accesses.length ? project.accesses[0]?.id : false;

        this.availableAccesses.forEach(access => {
            if (project.accesses && project.accesses.findIndex((_access: { id: string; }) => _access.id == access.id) == -1)
                project.accesses.push(access);
        });
    }

    canAssignRole(roleName: Role['name']): boolean {
        const loggedRole = this.loggedUser.role?.name;

        if (loggedRole === 'dev' || loggedRole === 'admin') return true;
        if (loggedRole === 'userAdmin') return ['guest', 'user', 'userAdmin'].includes(roleName);
        return false;
    }

    resolveRoleName(role: Role | string): Role['name'] {
        if (typeof role === 'object' && role?.name) return role.name;
        return this.roles.find(r => r.id === role || r.name === role)?.name ?? 'user';
    }

    isEmailValid(email: string): boolean {
        return !!this.tools.isEmailValid(email);
    }

    canSave(): boolean {
        return this.isEmailValid(this.user.email) && (!this.user.password || this.tools.isPasswordValid(this.user.password));
    }

    isAuthMethodAllowed(method: AuthMethod): boolean {
        return this.user.allowedAuthMethods?.includes(method) ?? true;
    }

    toggleAuthMethod(method: AuthMethod) {
        if (!this.canEditAuthMethods())
            return;

        if (!this.user.allowedAuthMethods)
            this.user.allowedAuthMethods = [];

        const index = this.user.allowedAuthMethods.indexOf(method);

        if (index >= 0) {
            if (this.user.allowedAuthMethods.length === 1) {
                this.pop.warning(this.translate.instant('users.auth_method_last_one'));
                return;
            }

            this.user.allowedAuthMethods.splice(index, 1);
        } else {
            this.user.allowedAuthMethods.push(method);
        }
    }

    onAccessChange(event: MatSelectChange, project: Project) {
        if (!this.user.accesses)
            this.user.accesses = [];

        let accessIndex = this.user.accesses.findIndex(a => a.project == project.id);

        if (accessIndex == -1 && event.value) {
            this.user.accesses.push({ project: project.id, access: event.value });
        } else if (accessIndex >= 0 && !event.value) {
            this.user.accesses.splice(accessIndex, 1);
        } else if (accessIndex >= 0 && event.value) {
            this.user.accesses.splice(accessIndex, 1, { project: project.id, access: event.value });
        }
    }

    handlePageEvent(e: PageEvent) {
        this.loadProjects();
    }

    resetForm() {
        this.user = { ...defaultUser };
        this.passwordVisible = false;
        this.selectedRole = 'user';
        this.user.allowedAuthMethods = this.loggedUser.allowedAuthMethods?.length ? [...this.loggedUser.allowedAuthMethods] : ['password', 'google', 'microsoft'];
        this.form = {};
        this.projects.forEach(p => p.selected = false);
        this.searchText = '';

        if (this.userForm) {
            this.userForm.resetForm();
        }
    }

    get passwordMissingRules(): string[] {
        return this.tools.getPasswordMissingRules(this.user.password ?? '');
    }

    get passwordMinLength(): number {
        return this.tools.passwordMinLength;
    }

    generatePassword() {
        this.user.password = this.tools.generatePassword();
        this.passwordVisible = true;
    }

    resendInvitation() {
        if (!this.user.id) return;

        this.loading = true;
        this.userService.resendInvitation(this.user.id).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res) => {
                this.loading = false;
                this.pop.success(this.translate.instant('users.invitation_resent_success'));
            },
            error: (err) => {
                this.loading = false;
                this.pop.error(this.translate.instant('users.invitation_resent_error') + ": " + (err.error ? err.error : err.toString()));
            }
        });
    }
}